import React from "react";
import { Link, Navigate } from "react-router-dom";
import { IconArrowLeft } from "@tabler/icons-react";
import Logo from "../images/mercadoagentes_logo.png";
import { useWhitelabel } from "../contexts/WhitelabelContext";
import "./LegalDoc.css";

const PrivacyPolicy = () => {
  const { isWhitelabel } = useWhitelabel();

  if (isWhitelabel) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="legal-page">
      <div className="legal-container">
        <div className="legal-header">
          <Link to="/login" className="legal-back-link">
            <IconArrowLeft size={16} />
            Voltar para o login
          </Link>
          <img src={Logo} alt="MercadoAgentes" className="legal-logo" />
          <h1 className="legal-title">Política de Privacidade</h1>
          <p className="legal-updated">Última atualização: 12/01/2026</p>
        </div>

        <div className="legal-summary">
          <strong>Resumo desta Política</strong>
          Esta política explica, de forma simples e transparente, como a
          MercadoAgentes (UPSOFT TECNOLOGIA LTDA.) coleta, utiliza, armazena e
          protege os seus dados pessoais quando você usa nossa plataforma. Seus
          dados são tratados com base na Lei Geral de Proteção de Dados (Lei
          nº 13.709/2018) e utilizados exclusivamente para a prestação e
          melhoria dos nossos serviços.
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Quem Somos</h2>
            <p>
              A MercadoAgentes é o nome fantasia da UPSOFT TECNOLOGIA LTDA.,
              inscrita no CNPJ sob o nº 61.304.434/0001-70. Somos uma plataforma
              SaaS (Software como Serviço) voltada para a criação, gestão e
              disponibilização de agentes de inteligência artificial.
            </p>
            <p>
              Para fins da LGPD, atuamos como <strong>Controladora</strong>{" "}
              quando definimos as finalidades e meios de tratamento dos seus
              dados, e como <strong>Operadora</strong> quando tratamos dados em
              nome do contratante (você).
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Dados que Coletamos</h2>
            <p>
              Coletamos apenas os dados estritamente necessários para a
              prestação dos nossos serviços. Veja abaixo o que coletamos e por
              quê:
            </p>

            <h3>2.1 Dados de Cadastro</h3>
            <dl className="legal-data-table">
              <dt>CPF ou CNPJ</dt>
              <dd>Identificação do titular do contrato</dd>
              <dt>Nome completo / Razão social</dt>
              <dd>Identificação do contratante</dd>
              <dt>E-mail</dt>
              <dd>Comunicações, notificações e acesso à conta</dd>
              <dt>Telefone</dt>
              <dd>Suporte e comunicações urgentes</dd>
              <dt>Nome do responsável</dt>
              <dd>Identificação da pessoa física responsável</dd>
              <dt>CPF do responsável</dt>
              <dd>Vinculação legal da contratação</dd>
              <dt>Endereço</dt>
              <dd>Envio de documentos e notificações</dd>
            </dl>

            <h3>2.2 Dados de Pagamento</h3>
            <p>
              Caso opte pelo pagamento via cartão de crédito, os dados do cartão
              serão solicitados no ato da contratação. Esses dados são
              processados por meio de gateway de pagamento seguro e não são
              armazenados diretamente pela MercadoAgentes.
            </p>

            <h3>2.3 Dados de Uso da Plataforma</h3>
            <p>
              Coletamos informações sobre como você utiliza a plataforma
              (padrões de uso, volume de transações, logs de acesso) para fins
              de segurança, auditoria e melhoria dos serviços.
            </p>

            <h3>2.4 Cookies</h3>
            <p>
              Utilizamos cookies para facilitar a navegação e coletar dados
              estatísticos que nos ajudam a melhorar os serviços. Você pode
              configurar seu navegador para bloquear cookies, mas isso pode
              reduzir a qualidade da sua experiência.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. Como Usamos Seus Dados</h2>
            <p>
              Seus dados pessoais são utilizados para as seguintes finalidades:
            </p>
            <ul>
              <li>
                <strong>Execução do contrato:</strong> cadastro, ativação de
                conta, cobranças e prestação dos serviços contratados.
              </li>
              <li>
                <strong>Comunicação:</strong> envio de avisos administrativos,
                notificações sobre limites de uso, alterações de plano e
                informações sobre o serviço.
              </li>
              <li>
                <strong>Segurança e auditoria:</strong> monitoramento de uso
                para verificar conformidade com os Termos de Uso e prevenção de
                fraudes.
              </li>
              <li>
                <strong>Melhoria dos serviços:</strong> análise de padrões de
                uso para aprimorar funcionalidades e desenvolver novos recursos.
              </li>
              <li>
                <strong>Cumprimento legal:</strong> atendimento a obrigações
                legais, ordens judiciais e requisições de autoridades
                competentes.
              </li>
              <li>
                <strong>Proteção ao crédito:</strong> compartilhamento com
                órgãos de proteção ao crédito para garantir a adimplência.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Base Legal para o Tratamento</h2>
            <p>
              O tratamento dos seus dados é fundamentado nas seguintes bases
              legais previstas no artigo 7º da LGPD:
            </p>
            <ul>
              <li>
                <strong>Execução de contrato (art. 7º, V):</strong> para a
                prestação dos serviços contratados.
              </li>
              <li>
                <strong>Legítimo interesse (art. 7º, IX):</strong> para melhoria
                dos serviços, segurança da plataforma e prevenção de fraudes.
              </li>
              <li>
                <strong>Cumprimento de obrigação legal (art. 7º, II):</strong>{" "}
                para atendimento a deveres legais e regulatórios.
              </li>
              <li>
                <strong>Consentimento (art. 7º, I):</strong> quando aplicável,
                para finalidades específicas comunicadas no ato da coleta.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Compartilhamento de Dados</h2>
            <p>
              Seus dados poderão ser compartilhados com terceiros apenas nas
              seguintes hipóteses:
            </p>
            <ul>
              <li>
                Órgãos de segurança pública, mediante solicitação legal
                pertinente, inclusive para identificação de terrorismo;
              </li>
              <li>
                Autoridades administrativas e judiciais, no âmbito de suas
                competências, com base no cumprimento do dever legal;
              </li>
              <li>
                Órgãos de proteção ao crédito, para garantir a adimplência do
                contratante;
              </li>
              <li>
                Parceiros e fornecedores da MercadoAgentes, estritamente para a
                execução dos serviços (ex.: provedores de infraestrutura,
                gateways de pagamento);
              </li>
              <li>
                Terceiros legalmente legítimos para defender interesses da
                MercadoAgentes ou do próprio contratante.
              </li>
            </ul>
            <p>
              <strong>
                Não vendemos, alugamos ou comercializamos seus dados pessoais a
                terceiros para fins de marketing.
              </strong>
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Armazenamento e Retenção de Dados</h2>
            <p>
              Seus dados são armazenados em ambiente seguro, com medidas
              técnicas e organizacionais em conformidade com os melhores padrões
              da indústria.
            </p>
            <ul>
              <li>
                <strong>Durante o contrato:</strong> seus dados são mantidos
                enquanto a relação contratual estiver ativa.
              </li>
              <li>
                <strong>Após cancelamento:</strong> os dados permanecem
                armazenados por até 60 dias para que você possa exportá-los.
                Após esse prazo, poderão ser excluídos definitivamente.
              </li>
              <li>
                <strong>Guarda legal:</strong> documentos fiscais e contratuais
                são mantidos por 5 (cinco) anos, conforme legislação civil e
                fiscal.
              </li>
              <li>
                <strong>Obrigação legal:</strong> dados poderão ser retidos
                além dos prazos acima quando houver obrigação legal específica.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Seus Direitos como Titular</h2>
            <p>
              Em conformidade com o artigo 18 da LGPD, você possui os seguintes
              direitos sobre seus dados pessoais:
            </p>
            <ul>
              <li>
                <strong>Confirmação e acesso:</strong> saber se tratamos seus
                dados e obter cópia das informações.
              </li>
              <li>
                <strong>Correção:</strong> solicitar a atualização de dados
                incompletos, inexatos ou desatualizados.
              </li>
              <li>
                <strong>Anonimização ou bloqueio:</strong> solicitar o
                tratamento restrito de dados desnecessários ou excessivos.
              </li>
              <li>
                <strong>Eliminação:</strong> solicitar a exclusão de dados
                coletados com base no seu consentimento.
              </li>
              <li>
                <strong>Portabilidade:</strong> solicitar a transferência dos
                seus dados a outro fornecedor de serviço.
              </li>
              <li>
                <strong>Revogação do consentimento:</strong> revogar o
                consentimento a qualquer momento, ciente de que isso poderá
                impactar a prestação dos serviços.
              </li>
            </ul>
            <p>
              Para exercer qualquer desses direitos, entre em contato pelo
              e-mail{" "}
              <a href="mailto:contato@mercadoagentes.com">
                contato@mercadoagentes.com
              </a>
              .
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Segurança dos Dados</h2>
            <p>
              Adotamos medidas técnicas e organizacionais adequadas para
              proteger seus dados pessoais contra acessos não autorizados,
              destruição, perda, alteração ou vazamento. Contudo, nenhum sistema
              de segurança é 100% infalível, e não nos responsabilizamos por
              vazamentos decorrentes de ataques que utilizem recursos
              tecnológicos superiores aos padrões usualmente aceitáveis pela
              indústria.
            </p>
            <p>
              Em caso de incidente de segurança que envolva risco ou dano
              relevante aos titulares, nos comprometemos a comunicá-lo em prazo
              razoável, informando quais dados foram afetados.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Conteúdo e Inteligência Artificial</h2>
            <p>
              Ao enviar conteúdo para a plataforma (textos, imagens, documentos,
              bases de conhecimento), você concede à MercadoAgentes uma licença
              limitada para usar, hospedar e processar esse conteúdo
              exclusivamente para a operação e melhoria dos serviços. Você
              mantém a titularidade sobre o conteúdo original inserido.
            </p>
            <p>
              Os agentes de IA criados na plataforma utilizam modelos de
              linguagem de terceiros. As respostas geradas são probabilísticas e
              podem conter imprecisões. A MercadoAgentes não se responsabiliza
              por decisões tomadas com base nas respostas dos agentes.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Alterações nesta Política</h2>
            <p>
              Esta política poderá ser atualizada a qualquer momento para
              refletir mudanças nos nossos serviços ou na legislação aplicável.
              Alterações substanciais serão comunicadas com antecedência mínima
              de 30 (trinta) dias. Recomendamos que você revise esta política
              periodicamente.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contato</h2>
            <p>
              Se você tiver dúvidas sobre esta Política de Privacidade ou sobre
              o tratamento dos seus dados pessoais, entre em contato conosco:
            </p>
            <div className="legal-contact-card">
              <dl>
                <dt>Empresa</dt>
                <dd>UPSOFT TECNOLOGIA LTDA. (MercadoAgentes)</dd>
                <dt>CNPJ</dt>
                <dd>61.304.434/0001-70</dd>
                <dt>E-mail</dt>
                <dd>
                  <a href="mailto:contato@mercadoagentes.com">
                    contato@mercadoagentes.com
                  </a>
                </dd>
                <dt>WhatsApp</dt>
                <dd>(47) 99160-0107</dd>
                <dt>Foro</dt>
                <dd>Comarca de Balneário Camboriú/SC</dd>
              </dl>
            </div>
          </section>
        </div>

        <div className="legal-footer">
          <strong>UPSOFT TECNOLOGIA LTDA.</strong>
          CNPJ: 61.304.434/0001-70 | contato@mercadoagentes.com
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
